import { Button } from '@rental/ui'
import { type ComponentProps, type ReactNode, useId, useRef } from 'react'
import { useScrollState } from '../scroll'
import { Section } from './Section'
import { ReasonList } from './SubPanel'
import { ProblemNotice } from './ui'

/** A form with a rail: its steps, and from 1024 px the rail beside them. */
export const RAIL_FORM = 'flex flex-col gap-6 lg:flex-row lg:items-start'
/** The steps beside the rail. */
export const RAIL_STEPS = 'flex min-w-0 flex-1 flex-col gap-6'

/**
 * A screen's one action, with what it waits for (DESIGN.md, "A screen and its
 * parts"): the hand-over's and the return's. From 1024 px it stands beside the
 * steps and stays in sight while they scroll, never taller than the screen;
 * below, it follows them. On top, always in sight: the one button, what the
 * form lacks, and a refusal. Under them, scrolling inside the rail when they do
 * not fit: what the screen puts there, such as the blocks and what is owed.
 * Nothing here is rendered twice and nothing here is a new live region.
 */
export function ActionRail({
  title,
  action,
  detailsLabel,
  ready,
  busy,
  reasons,
  stoppedBy = [],
  problem,
  notice,
  children,
}: {
  title: string
  /** The button's words: Nuova prenotazione's carry the total, in figures. */
  action: ReactNode
  /** The name of the part under the button, while it scrolls. */
  detailsLabel: string
  ready: boolean
  busy: boolean
  /** What the form lacks, in words, in the page's order. */
  reasons: readonly string[]
  /** The ids of the other lists in the rail that stop the button, such as the blocks. */
  stoppedBy?: readonly string[]
  problem: ComponentProps<typeof ProblemNotice>['problem']
  /** What the last press met that a problem's words cannot say, in their place: a booking's refusals. */
  notice?: ReactNode
  children: ReactNode
}) {
  const reasonsId = useId()
  // What scrolls takes the focus, so a keyboard can scroll it (WCAG 2.1.1), and only then.
  const details = useRef<HTMLDivElement>(null)
  const { scrolls, moreBelow } = useScrollState(details)
  // The button names what stops it: the form's reasons and the lists still standing.
  const describedBy =
    busy || ready
      ? undefined
      : [...(reasons.length > 0 ? [reasonsId] : []), ...stoppedBy].join(' ') || undefined
  return (
    <Section title={title} className="action-rail lg:sticky lg:top-8 lg:w-2xs lg:shrink-0 xl:w-xs">
      <div className="flex flex-col gap-2">
        <div>
          <Button
            type="submit"
            variant="staff"
            className="w-full shadow-action disabled:shadow-none sm:w-auto lg:w-full"
            busy={busy}
            disabled={!ready && !busy}
            aria-describedby={describedBy}
          >
            {action}
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </div>
      <ProblemNotice problem={problem} />
      {notice}
      <div
        ref={details}
        data-more={moreBelow ? '' : undefined}
        {...(scrolls ? { role: 'region', 'aria-label': detailsLabel, tabIndex: 0 } : {})}
        // Positioned, as ScrollRegion is: hidden words below the fold stay inside what scrolls.
        className="scroll-fade relative flex min-h-0 flex-col gap-3 scroll-pb-8 lg:overflow-y-auto"
      >
        {children}
      </div>
    </Section>
  )
}
